<template>
  <main dir="rtl" class="min-h-screen bg-slate-50 text-slate-900">
    <header class="bg-white border-b border-slate-200 sticky top-0 z-30">
      <div class="max-w-[1600px] mx-auto px-6 py-4 flex flex-wrap items-center justify-between gap-4">
        <div class="flex items-center gap-3">
          <div class="bg-primary p-2 rounded-lg text-white">
            <span class="material-icons-round">calendar_today</span>
          </div>
          <h1 class="text-xl font-bold">جدول المعلمين الأسبوعي</h1>
        </div>
        <div class="flex items-center gap-4 flex-grow md:flex-grow-0">
          <div class="relative flex-grow min-w-[200px]">
            <span class="absolute right-3 top-1/2 -translate-y-1/2 material-icons-round text-slate-400">search</span>
            <input
              v-model="searchQuery"
              class="w-full pr-10 pl-4 py-2 rounded-lg border-slate-200 bg-slate-50 focus:ring-primary focus:border-primary"
              placeholder="البحث عن معلم أو فصل..."
              type="text"
            />
          </div>
          <select
            v-model="selectedSchedule"
            class="rounded-lg border-slate-200 bg-slate-50 focus:ring-primary"
          >
            <option v-for="schedule in scheduleOptions" :key="schedule.id" :value="schedule.id">
              {{ schedule.title }}
            </option>
          </select>
          <select
            v-model="selectedTeacher"
            class="rounded-lg border-slate-200 bg-slate-50 focus:ring-primary"
          >
            <option v-for="teacher in teacherOptions" :key="teacher" :value="teacher">{{ teacher }}</option>
          </select>
        </div>
      </div>
    </header>

    <section class="max-w-[1600px] mx-auto p-6">
      <div v-if="loading" class="bg-white border border-slate-200 rounded-xl p-5 text-center text-slate-600 mb-6">
        جاري تحميل جدول الجلسات الأسبوعي...
      </div>
      <div v-if="errorMessage" class="bg-red-50 border border-red-200 rounded-xl p-4 text-red-700 mb-6">
        {{ errorMessage }}
      </div>

      <div class="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        <div class="bg-white p-4 rounded-xl border border-slate-200 flex items-center gap-4">
          <div class="w-12 h-12 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center">
            <span class="material-icons-round">people</span>
          </div>
          <div>
            <p class="text-xs text-slate-500">إجمالي المعلمين</p>
            <p class="text-lg font-bold">{{ teacherOptions.length }} معلم</p>
          </div>
        </div>
        <div class="bg-white p-4 rounded-xl border border-slate-200 flex items-center gap-4">
          <div class="w-12 h-12 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center">
            <span class="material-icons-round">meeting_room</span>
          </div>
          <div>
            <p class="text-xs text-slate-500">الفصول المتاحة</p>
            <p class="text-lg font-bold">{{ classroomCount }} فصل</p>
          </div>
        </div>
        <div class="bg-white p-4 rounded-xl border border-slate-200 flex items-center gap-4">
          <div class="w-12 h-12 bg-amber-100 text-amber-600 rounded-full flex items-center justify-center">
            <span class="material-icons-round">history</span>
          </div>
          <div>
            <p class="text-xs text-slate-500">ساعات العمل</p>
            <p class="text-lg font-bold">{{ totalHoursPerWeek.toFixed(1) }} ساعة/أسبوع</p>
          </div>
        </div>
        <div class="bg-white p-4 rounded-xl border border-slate-200 flex items-center gap-4">
          <div class="w-12 h-12 bg-purple-100 text-purple-600 rounded-full flex items-center justify-center">
            <span class="material-icons-round">event_available</span>
          </div>
          <div>
            <p class="text-xs text-slate-500">الحالة الحالية</p>
            <p class="text-lg font-bold text-emerald-600">نشط الآن</p>
          </div>
        </div>
      </div>

      <div class="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm">
        <div class="schedule-grid border-b border-slate-200 bg-slate-50">
          <div class="p-4 border-l border-slate-200 font-bold text-center text-slate-500">الصلاة</div>
          <div
            v-for="weekday in weekdays"
            :key="weekday.id"
            class="p-4 border-l border-slate-200 font-bold text-center"
          >
            {{ weekday.label }}
          </div>
        </div>

        <div class="overflow-x-auto">
          <div
            v-for="prayer in visiblePrayerRows"
            :key="prayer.key"
            class="schedule-grid border-b border-slate-100 min-h-[120px]"
          >
            <div class="p-4 border-l border-slate-200 bg-slate-50 flex flex-col items-center justify-center gap-1">
              <span class="text-sm font-bold">{{ prayer.label }}</span>
            </div>

            <div
              v-for="weekday in weekdays"
              :key="`${prayer.key}-${weekday.id}`"
              class="p-2 border-l border-slate-100"
            >
              <div class="space-y-2">
                <div
                  v-for="lesson in lessonsByPrayerAndDay(prayer.key, weekday.id)"
                  :key="lesson.id"
                  class="p-3 rounded-lg h-full border-r-4 overflow-hidden min-w-0"
                  :class="lessonColorClass(lesson.color)"
                >
                  <p class="lesson-card-title mb-1">{{ lesson.subject }}</p>
                  <div class="lesson-card-line text-slate-600">
                    <span class="material-icons-round lesson-card-icon">schedule</span>
                    {{ lessonTimeLabel(lesson) }}
                  </div>
                  <p class="lesson-card-line text-slate-500 mt-1">
                    {{ lessonActualTimeLabel(lesson) }}
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div v-if="!selectedScheduleLessons.length" class="p-8 text-center text-slate-500">
            لا توجد حصص مطابقة للجدول المحدد.
          </div>
          <div v-else-if="!filteredLessons.length" class="p-6 text-center text-slate-500 border-t border-slate-100">
            لا توجد حصص للمعلم المختار داخل هذا الجدول.
          </div>
        </div>
      </div>

      <div class="mt-6 flex flex-wrap items-center justify-between gap-4">
        <div class="flex items-center gap-2">
          <button
            @click="goPreviousWeek"
            class="p-2 rounded-lg border border-slate-200 hover:bg-slate-100 transition-colors"
          >
            <span class="material-icons-round">chevron_right</span>
          </button>
          <span class="text-sm font-medium">الأسبوع الحالي: {{ weekLabel }}</span>
          <button
            @click="goNextWeek"
            class="p-2 rounded-lg border border-slate-200 hover:bg-slate-100 transition-colors"
          >
            <span class="material-icons-round">chevron_left</span>
          </button>
        </div>
        <div class="flex items-center gap-3">
          <button
            @click="notifyExport('PDF')"
            class="flex items-center gap-2 px-4 py-2 text-sm font-medium text-slate-600 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
          >
            <span class="material-icons-round text-lg">download</span>
            <span>تحميل الجدول PDF</span>
          </button>
          <button
            @click="notifyExport('print')"
            class="flex items-center gap-2 px-4 py-2 text-sm font-medium text-slate-600 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
          >
            <span class="material-icons-round text-lg">print</span>
            <span>طباعة</span>
          </button>
        </div>
      </div>
    </section>
  </main>
</template>

<script>
import { defineComponent } from 'vue';
import { listIqraSchedules } from '@/services/iqraSchedules';

export default defineComponent({
  name: 'IqraTeacherWeeklySchedule',
  data() {
    return {
      loading: false,
      errorMessage: '',
      searchQuery: '',
      selectedSchedule: '',
      selectedTeacher: '',
      weekOffset: 0,
      weekdays: [
        { id: 7, label: 'الأحد' },
        { id: 1, label: 'الإثنين' },
        { id: 2, label: 'الثلاثاء' },
        { id: 3, label: 'الأربعاء' },
        { id: 4, label: 'الخميس' },
        { id: 5, label: 'الجمعة' },
        { id: 6, label: 'السبت' },
      ],
      prayerRows: [
        { key: 'fajr', label: 'صلاة الفجر', order: 1 },
        { key: 'dhuhr', label: 'صلاة الظهر', order: 2 },
        { key: 'asr', label: 'صلاة العصر', order: 3 },
        { key: 'maghrib', label: 'صلاة المغرب', order: 4 },
        { key: 'isha', label: 'صلاة العشاء', order: 5 },
      ],
      lessons: [],
      iqraSchedules: [],
      scheduleTeachersMap: {},
    };
  },
  async mounted() {
    await this.loadIqraWeeklySchedule();
  },
  computed: {
    scheduleOptions() {
      return this.iqraSchedules;
    },
    selectedScheduleLessons() {
      return this.lessons.filter((lesson) => String(lesson.scheduleId) === String(this.selectedSchedule));
    },
    filteredLessons() {
      const search = this.searchQuery.trim().toLowerCase();

      return this.selectedScheduleLessons.filter((lesson) => {
        const matchTeacher = lesson.teacher === this.selectedTeacher;
        const matchSearch = !search
          || lesson.teacher.toLowerCase().includes(search)
          || lesson.subject.toLowerCase().includes(search)
          || lesson.classroom.toLowerCase().includes(search)
          || lesson.scheduleTitle.toLowerCase().includes(search);
        return matchTeacher && matchSearch;
      });
    },
    visiblePrayerRows() {
      if (!this.selectedSchedule) return [];
      const rows = [...this.prayerRows];
      const hasFixedRows = this.selectedScheduleLessons.some((lesson) => lesson.prayerKey === 'fixed');
      if (hasFixedRows) {
        rows.push({ key: 'fixed', label: 'أوقات ثابتة', order: 6 });
      }
      return rows.sort((a, b) => a.order - b.order);
    },
    teacherOptions() {
      const fromLessons = this.lessons
        .filter((lesson) => String(lesson.scheduleId) === String(this.selectedSchedule))
        .map((lesson) => lesson.teacher)
        .filter((teacher) => teacher && teacher !== 'غير محدد');
      const fromScheduleTeachers = this.scheduleTeachersMap[this.selectedSchedule] || [];
      return [...new Set([...fromLessons, ...fromScheduleTeachers])];
    },
    classroomCount() {
      return [...new Set(this.filteredLessons.map((lesson) => lesson.classroom))].length;
    },
    totalHoursPerWeek() {
      const totalMinutes = this.filteredLessons.reduce((sum, lesson) => sum + (lesson.durationMinutes || 0), 0);
      return totalMinutes / 60;
    },
    weekLabel() {
      const now = new Date();
      const currentDay = now.getDay();
      const sunday = new Date(now);
      sunday.setDate(now.getDate() - currentDay + this.weekOffset * 7);
      const saturday = new Date(sunday);
      saturday.setDate(sunday.getDate() + 6);

      const formatDate = (date) => date.toLocaleDateString('ar-SA', { day: 'numeric', month: 'long', year: 'numeric' });
      return `${formatDate(sunday)} - ${formatDate(saturday)}`;
    },
  },
  methods: {
    async loadIqraWeeklySchedule() {
      this.loading = true;
      this.errorMessage = '';
      try {
        const result = await listIqraSchedules({
          is_active: true,
          page: 1,
          size: 100,
        });
        const schedules = result?.data || [];
        const lessons = [];
        const scheduleOptions = [];
        const scheduleTeachersMap = {};

        schedules.forEach((schedule) => {
          scheduleOptions.push({
            id: String(schedule.id),
            title: schedule.title || `جدول #${schedule.id}`,
          });

          const scheduleTeachers = (schedule.teachers || [])
            .filter((item) => item.is_active !== false)
            .map((item) => ({
              id: item.teacher?.id ?? null,
              name: item.teacher?.full_name || item.teacher?.fullName || '',
            }))
            .filter((item) => item.name)
            .map((item) => item.name)
            .filter(Boolean);
          scheduleTeachersMap[String(schedule.id)] = [...new Set(scheduleTeachers)];

          (schedule.days || []).forEach((day) => {
            if (day.is_active === false) return;
            (day.slots || []).forEach((slot) => {
              if (slot.is_active === false) return;
              const slotId = Number(slot.slot_order);

              const slotTeachers = (slot.slot_teachers || [])
                .filter((item) => item.is_active !== false)
                .map((item) => ({
                  id: item.teacher?.id ?? null,
                  name: item.teacher?.full_name || item.teacher?.fullName || '',
                  actualStartTime: item.actual_start_time || '',
                  actualEndTime: item.actual_end_time || '',
                  prayerSource: item.prayer_source || '',
                }))
                .filter((item) => item.name);

              const fallbackTeachers = scheduleTeachers;
              const lessonTeachers = slotTeachers.length
                ? slotTeachers
                : fallbackTeachers.map((name) => ({ id: null, name }));
              const safeTeachers = lessonTeachers.length ? lessonTeachers : [{ id: null, name: 'غير محدد' }];

              safeTeachers.forEach((teacher, teacherIndex) => {
                lessons.push({
                  id: `${schedule.id}-${day.id}-${slot.id}-${teacherIndex}`,
                  scheduleId: String(schedule.id),
                  weekday: Number(day.weekday),
                  slotId,
                  subject: slot.slot_name || 'فترة الجلسة',
                  teacher: teacher.name,
                  teacherId: teacher.id ? Number(teacher.id) : null,
                  classroom: schedule.title || 'جدول الجلسات',
                  scheduleTitle: schedule.title || '',
                  slotOrder: Number(slot.slot_order) || 0,
                  startMode: slot.start_mode,
                  fixedStartTime: slot.fixed_start_time,
                  prayerName: slot.prayer_name,
                  prayerKey: this.normalizePrayerName(slot.prayer_name),
                  offsetMinutes: slot.offset_minutes,
                  durationMinutes: Number(slot.duration_minutes) || 0,
                  actualStartTime: teacher.actualStartTime || slot.actual_start_time || '',
                  actualEndTime: teacher.actualEndTime || slot.actual_end_time || '',
                  prayerSource: teacher.prayerSource || slot.prayer_source || '',
                  color: this.colorFromName(teacher.name),
                });
              });
            });
          });
        });

        this.lessons = lessons;
        this.scheduleTeachersMap = scheduleTeachersMap;
        this.iqraSchedules = scheduleOptions;
        if (!this.scheduleOptions.length) {
          this.selectedSchedule = '';
          this.selectedTeacher = '';
        } else if (!this.scheduleOptions.some((schedule) => String(schedule.id) === String(this.selectedSchedule))) {
          this.selectedSchedule = this.scheduleOptions[0].id;
        }

        if (!this.teacherOptions.length) {
          this.selectedTeacher = '';
        } else if (!this.teacherOptions.includes(this.selectedTeacher)) {
          this.selectedTeacher = this.teacherOptions[0];
        }
      } catch (error) {
        this.errorMessage = error?.message || 'فشل في تحميل جدول الجلسات الأسبوعي';
      } finally {
        this.loading = false;
      }
    },
    lessonsByPrayerAndDay(prayerKey, weekday) {
      return this.filteredLessons
        .filter((lesson) => lesson.prayerKey === prayerKey && lesson.weekday === weekday)
        .sort((a, b) => this.lessonSortWeight(a) - this.lessonSortWeight(b));
    },
    normalizePrayerName(prayerName) {
      const normalized = String(prayerName || '').trim().toLowerCase();
      if (['fajr', 'dhuhr', 'asr', 'maghrib', 'isha'].includes(normalized)) {
        return normalized;
      }
      return 'fixed';
    },
    lessonSortWeight(lesson) {
      if (lesson.startMode === 'fixed_time' && lesson.fixedStartTime) {
        const [hoursText, minutesText] = String(lesson.fixedStartTime).split(':');
        const hours = Number(hoursText);
        const minutes = Number(minutesText);
        if (!Number.isNaN(hours) && !Number.isNaN(minutes)) {
          return (hours * 60) + minutes;
        }
      }
      const offset = Number(lesson.offsetMinutes);
      if (!Number.isNaN(offset)) {
        return offset;
      }
      return Number(lesson.slotOrder) || Number(lesson.slotId) || 0;
    },
    lessonTimeLabel(lesson) {
      if (lesson.startMode === 'fixed_time' && lesson.fixedStartTime) {
        return `وقت ثابت (${this.formatTimeArabic(lesson.fixedStartTime)})`;
      }

      if (lesson.startMode === 'prayer_offset') {
        const prayerLabel = this.prayerLabel(lesson.prayerName || lesson.prayerKey);
        const offset = Number(lesson.offsetMinutes);

        if (Number.isNaN(offset) || offset === 0) {
          return `وقت ${prayerLabel}`;
        }

        const direction = offset > 0 ? 'بعد' : 'قبل';
        return `${direction} ${prayerLabel} بـ ${Math.abs(offset)} دقيقة`;
      }

      return `الفترة ${lesson.slotOrder || lesson.slotId}`;
    },
    lessonActualTimeLabel(lesson) {
      const actual = this.lessonActualTime(lesson);
      if (!actual) return 'الوقت الفعلي: غير متاح';
      return `الوقت الفعلي: ${this.formatTimeArabic(actual)}`;
    },
    lessonActualTime(lesson) {
      if (lesson.actualStartTime) {
        return String(lesson.actualStartTime).slice(0, 8);
      }
      if (lesson.startMode === 'fixed_time' && lesson.fixedStartTime) {
        return String(lesson.fixedStartTime).slice(0, 8);
      }

      return '';
    },
    formatTimeArabic(timeValue) {
      const value = String(timeValue || '').slice(0, 5);
      const [hoursText, minutesText] = value.split(':');
      const hours = Number(hoursText);
      const minutes = Number(minutesText);
      if (Number.isNaN(hours) || Number.isNaN(minutes)) return value || '-';

      const period = hours >= 12 ? 'م' : 'ص';
      const hour12 = hours % 12 === 0 ? 12 : hours % 12;
      return `${hour12}:${String(minutes).padStart(2, '0')} ${period}`;
    },
    prayerLabel(prayerValue) {
      const key = String(prayerValue || '').trim().toLowerCase();
      const map = {
        fajr: 'الفجر',
        dhuhr: 'الظهر',
        asr: 'العصر',
        maghrib: 'المغرب',
        isha: 'العشاء',
      };
      return map[key] || 'الصلاة';
    },
    slotStartLabel(slot) {
      if (slot.start_mode === 'fixed_time' && slot.fixed_start_time) {
        return String(slot.fixed_start_time).slice(0, 5);
      }
      if (slot.start_mode === 'prayer_offset' && slot.prayer_name) {
        return `صلاة ${slot.prayer_name}`;
      }
      return `الفترة ${slot.slot_order}`;
    },
    slotEndLabel(slot) {
      const duration = Number(slot.duration_minutes) || 0;
      if (slot.start_mode === 'fixed_time' && slot.fixed_start_time && duration > 0) {
        return this.addMinutes(String(slot.fixed_start_time).slice(0, 5), duration);
      }
      if (duration > 0) {
        return `${duration} دقيقة`;
      }
      return '-';
    },
    addMinutes(timeValue, minutesToAdd) {
      const [hoursText, minutesText] = String(timeValue).split(':');
      const hours = Number(hoursText);
      const minutes = Number(minutesText);
      if (Number.isNaN(hours) || Number.isNaN(minutes)) return timeValue;
      const total = (hours * 60) + minutes + minutesToAdd;
      const normalized = ((total % 1440) + 1440) % 1440;
      const outHours = String(Math.floor(normalized / 60)).padStart(2, '0');
      const outMinutes = String(normalized % 60).padStart(2, '0');
      return `${outHours}:${outMinutes}`;
    },
    colorFromName(name) {
      const palette = ['blue', 'amber', 'emerald', 'purple', 'indigo', 'rose'];
      const value = String(name || '');
      let hash = 0;
      for (let i = 0; i < value.length; i += 1) {
        hash = ((hash << 5) - hash) + value.charCodeAt(i);
        hash |= 0;
      }
      const index = Math.abs(hash) % palette.length;
      return palette[index];
    },
    lessonColorClass(color) {
      const map = {
        blue: 'bg-blue-50 border-blue-500 text-blue-700',
        amber: 'bg-amber-50 border-amber-500 text-amber-700',
        emerald: 'bg-emerald-50 border-emerald-500 text-emerald-700',
        purple: 'bg-purple-50 border-purple-500 text-purple-700',
        indigo: 'bg-indigo-50 border-indigo-500 text-indigo-700',
        rose: 'bg-rose-50 border-rose-500 text-rose-700',
      };
      return map[color] || map.blue;
    },
    goPreviousWeek() {
      this.weekOffset -= 1;
    },
    goNextWeek() {
      this.weekOffset += 1;
    },
    notifyAddLesson() {
      alert('لإضافة الحصص أو تعديلها استخدم شاشة جداول الجلسات.');
    },
    notifyExport(type) {
      alert(type === 'PDF' ? 'سيتم دعم تحميل PDF قريبًا.' : 'استخدم طباعة المتصفح مؤقتًا.');
    },
  },
  watch: {
    selectedSchedule() {
      if (!this.teacherOptions.length) {
        this.selectedTeacher = '';
        return;
      }
      if (!this.teacherOptions.includes(this.selectedTeacher)) {
        this.selectedTeacher = this.teacherOptions[0];
      }
    },
  },
});
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Noto+Kufi+Arabic:wght@300;400;500;600;700&display=swap');
@import url('https://fonts.googleapis.com/icon?family=Material+Icons+Round');

.material-icons-round {
  font-size: 20px;
}

.schedule-grid {
  display: grid;
  grid-template-columns: 100px repeat(7, minmax(150px, 1fr));
}

.lesson-card-title {
  font-size: clamp(10px, 1.1vw, 12px);
  line-height: 1.25;
  font-weight: 700;
  white-space: normal;
  overflow-wrap: anywhere;
  word-break: break-word;
}

.lesson-card-line {
  display: flex;
  align-items: flex-start;
  gap: 4px;
  min-width: 0;
  font-size: clamp(9px, 1vw, 11px);
  line-height: 1.3;
  white-space: normal;
  overflow-wrap: anywhere;
  word-break: break-word;
}

.lesson-card-icon {
  font-size: 13px;
  flex: 0 0 auto;
  margin-top: 1px;
}

main {
  font-family: 'Noto Kufi Arabic', sans-serif;
}
</style>
